import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    InputLabel,
    TextField,
    Typography,
} from '@mui/material';
import clsx from 'clsx';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { CheckboxWithLabel } from 'formik-mui';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { MdClose, MdDelete, MdExpandMore } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { accountsSlice } from 'app/store';
import { SelectionAutocomplete } from 'components';
import { FormTextField } from 'components/TextField';
import { TinyMCEEditor } from 'components/TinyMCEEditor';
import { getAllLinkTypes, selectAssetLinkTypes, selectAssetLinkTypesStatus } from 'features/assets/assetManagerSlice';
import { addNewAsset } from 'features/assets/assetsTableSlice';
import { editAsset, getEditAsset } from 'features/assets/editAssetSlice';
import { fetchAudiencesAll, getAudienceStatus, selectAllAudiences } from 'features/audiences/audiencesSlice';
import {
    createCategory,
    fetchAllCategories,
    getAllCategories,
    getCategoryStatus,
    resetCategoryStatus,
} from 'features/category/categorySlice';
import { closePanel, selectFormInfo } from 'features/common/formSlice';
import { fetchGroups, getGroupStatus, resetGroupState, selectAllGroups } from 'features/groups/groupsSlice';
import {
    getAssetsPicklist,
    selectAssetsPicklist,
    selectAssetsPicklistLoadingStatus,
} from 'features/picklists/pickListsSlice';
import { setTableZoneStatus } from 'features/zones/zonesTableSlice';
import { ConfigValuesField } from 'forms/Components/ConfigValuesField';
import SetDirtyForm from 'forms/SetDirtyForm';
import { LoadingLogo } from 'GeminiViewerComponent/components/LoadingLogo/LoadingLogo';
import { SnackbarDismiss } from 'GeminiViewerComponent/components/SnackbarDismiss';
import { accordionStyles, panelStyles } from 'GeminiViewerComponent/components/styles';
import { clearActiveAsset } from 'GeminiViewerComponent/features/asset/assetSlice';
import {
    fetchAllCompanionApps,
    getAllCompanionApps,
    getCompanionLoadingStatus,
} from 'GeminiViewerComponent/features/companionApps/companionAppsSlice';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { AssetTypes } from 'GeminiViewerComponent/helpers';
import { LoadingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { snackbarHandler } from 'GeminiViewerComponent/helpers/snackbar-handler';
import { Role } from 'helpers';
import { LinkTypes } from 'helpers/enumerations';
import { FormActions } from 'helpers/form-action';
import { useTinyMCEInit } from 'hooks/useTinyMCEInit';
import {
    fetchAllHotspotIcons,
    fetchDefaultHotspots,
    getHotspotStatus,
    getItemHotspots,
    getNavigationHotspots,
} from 'shared/hotspotsSlice';
import { makeFormStyles } from '../styles';

const AssetForm = ({ formAction, assetId }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const init = useTinyMCEInit();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useSelector(selectDefaultTheme);
    const classes = makeFormStyles(theme);
    const styles = accordionStyles();
    const panelClasses = panelStyles(theme);
    const [loaded, setLoaded] = useState(
        formAction.id === false // FormActions.Create.id ? true : false
    );
    const formInfo = useSelector(selectFormInfo);
    const activeUser = useSelector(accountsSlice.selectActiveUser);
    const assetListStatus = useSelector(selectAssetsPicklistLoadingStatus);
    const readOnlyMode = activeUser?.role === Role.User ? true : false;
    const defaultItemHotspotIcon = useSelector((state) => state.hotspots.defaultItemHotspotIconId);
    const defaultNavHotspotIcon = useSelector((state) => state.hotspots.defaultNavHotspotIconId);
    const defaultHotspotStatus = useSelector((state) => state.hotspots.defaultHotspotsStatus);
    let assetLists = useSelector(selectAssetsPicklist);
    assetLists = assetLists.filter(
        (asset) => asset.asset_type_id !== AssetTypes.Procedure && asset.asset_id !== assetId
    );

    const INIT_VALUES = {
        display_name: '',
        description: '',
        disclaimer_tooltip: '',
        disclaimer_html: '',
        custom_data: '',
        asset_link_types: [],
        groups: [],
        categories: [],
        audiences: [],
        image: {},
        item_hotspot_icon_id: '',
        nav_hotspot_icon_id: '',
        asset_type_id: formInfo?.assetForm?.assetTypeID ?? AssetTypes.Place,
        config_json: [
            {
                key: '',
                value: '',
            },
        ],
        hide_nav_zones: false,
        hide_nav_search: false,
        hide_nav_title: false,
        hide_nav_controls: false,
        hide_viewer_layout_panel: false,
        pano_asset_type: formInfo?.assetForm?.assetTypeID,
        player_url: '',
        companion_apps: [],
        child_asset_ids: [],
        empty_item_opacity: 60,
        hide_empty_item_panel: false
    };
    const [initValues, setInitValues] = useState({ ...INIT_VALUES });

    useEffect(() => {
        // TODO: Test for reloading and prompt user to save changes or cancel
        // the load
        // const reloading = loaded;

        if (formAction.id === FormActions.Create.id) {
            const getAssetsTypes = async () => {
                const allLinkTypes = await dispatch(getAllLinkTypes());
                const link_types = {
                    asset_link_types: allLinkTypes.payload.link_types.map((a) => a.link_type_id),
                };

                setInitValues({
                    ...INIT_VALUES,
                    ...link_types,
                    nav_hotspot_icon_id: defaultNavHotspotIcon,
                    item_hotspot_icon_id: defaultItemHotspotIcon,
                });
                setLoaded(true);
            };
            getAssetsTypes();
        }
        if (formAction.id === FormActions.Edit.id) {
            const fetchData = async () => {
                var objectData = await dispatch(getEditAsset(assetId));
                var fetchedObject = objectData.payload;
                let fetched_options_field = fetchedObject?.options_json ? JSON.parse(fetchedObject?.options_json) : {};
                setInitValues({
                    display_name: fetchedObject.display_name,
                    description: fetchedObject.description,
                    disclaimer_tooltip: fetchedObject?.disclaimer_tooltip,
                    disclaimer_html: fetchedObject?.disclaimer_html,
                    custom_data: fetchedObject.custom_data,
                    player_url: fetchedObject.player_url || '',
                    asset_link_types: fetchedObject.asset_link_types?.map((x) => x.link_type.link_type_id),
                    companion_apps: fetchedObject.companion_apps?.map((x) => x.companion_app.companion_app_id),
                    groups: fetchedObject.groups?.map((x) => x.group.group_id),
                    categories: fetchedObject.categories?.map((x) => x.category_id),
                    audiences: fetchedObject.audiences?.map((x) => x.audience_id),
                    nav_hotspot_icon_id: fetchedObject.nav_hotspot_icon_id,
                    item_hotspot_icon_id: fetchedObject.item_hotspot_icon_id,
                    image: {},
                    image_url: fetchedObject.image_url,
                    config_json: fetchedObject.config_json ?? {
                        key: '',
                        value: '',
                    },
                    hide_nav_zones: !!fetched_options_field?.hide_nav_zones,
                    hide_nav_search: !!fetched_options_field?.hide_nav_search,
                    hide_nav_title: !!fetched_options_field?.hide_nav_title,
                    hide_nav_controls: !!fetched_options_field?.hide_nav_controls,
                    hide_viewer_layout_panel: !!fetched_options_field?.hide_viewer_layout_panel,
                    pano_asset_type:
                        fetchedObject.asset_type_id === AssetTypes.Product
                            ? AssetTypes.Product
                            : fetched_options_field?.pano_asset_type === undefined
                                ? AssetTypes.Place
                                : fetched_options_field?.pano_asset_type,
                    auto_view_single_link: fetchedObject?.auto_view_single_link,
                    child_asset_ids: fetchedObject?.children_ids || [],
                    empty_item_opacity: fetchedObject?.empty_item_opacity !== undefined && fetchedObject?.empty_item_opacity !== null ? fetchedObject.empty_item_opacity : 60,
                    hide_empty_item_panel: !!fetched_options_field?.hide_empty_item_panel,
                });
                setLoaded(true);
            };
            fetchData();
        }
    }, [assetId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (defaultHotspotStatus == LoadingStatus.Idle) {
            dispatch(fetchDefaultHotspots());
        }

        setInitValues({
            ...initValues,
            nav_hotspot_icon_id: defaultNavHotspotIcon,
            item_hotspot_icon_id: defaultItemHotspotIcon,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultItemHotspotIcon, defaultNavHotspotIcon]);

    useEffect(() => {
        if (assetListStatus === LoadingStatus.Idle) {
            dispatch(getAssetsPicklist());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (values, setSubmitting, resetForm) => {
        dispatch(activateLoading({ showProgress: true }));
        values = cleanCategories(values);
        values.options_json = {
            hide_nav_zones: !!values.hide_nav_zones,
            hide_nav_search: !!values.hide_nav_search,
            hide_nav_title: !!values.hide_nav_title,
            hide_nav_controls: !!values.hide_nav_controls,
            hide_viewer_layout_panel: !!values.hide_viewer_layout_panel,
            pano_asset_type: values.pano_asset_type,
            hide_empty_item_panel: !!values.hide_empty_item_panel,
        };
        delete values?.hide_nav_zones;
        delete values?.hide_nav_search;
        delete values?.hide_nav_title;
        delete values?.hide_nav_controls;
        delete values?.hide_viewer_layout_panel;
        delete values?.hide_empty_item_panel;
        var resultAction = null;
        switch (formAction.id) {
            case FormActions.Edit.id:
                resultAction = await dispatch(
                    editAsset({
                        asset_id: assetId,
                        ...values,
                        dispatch,
                    })
                );
                if (!resultAction.error) {
                    dispatch(resetCategoryStatus());
                    dispatch(closePanel({ formKey: 'assetForm' }));
                    dispatch(resetGroupState());
                }
                break;
            case FormActions.Create.id:
                resultAction = await dispatch(addNewAsset({ ...values, dispatch }));
                if (!resultAction.error) {
                    dispatch(resetCategoryStatus());
                    dispatch(clearActiveAsset());
                    resetForm();
                    if (formInfo?.assetForm?.assetTypeID === AssetTypes.Product) {
                        navigate(`/asset/${resultAction?.payload?.asset_id}/videoimporter`);
                    } else if (formInfo?.assetForm?.assetTypeID === AssetTypes.Place) {
                        navigate(`/asset/${resultAction?.payload?.asset_id}/panoimporter`);
                    } else {
                        navigate(`/asset/${resultAction?.payload?.asset_id}`);
                    }
                }
                break;
            default:
                break;
        }

        dispatch(deactivateLoading());
        const { message, variant } = snackbarHandler(resultAction.meta.requestStatus, formAction.label);
        enqueueSnackbar(message, {
            action: (key) => <SnackbarDismiss key={key} />,
            variant: variant,
        });
        dispatch(setTableZoneStatus(LoadingStatus.Idle));
        setSubmitting(false);
    };

    const onDrop = useCallback(({ file, setFieldValue }) => {
        setFieldValue('image', file);

        const reader = new FileReader();

        reader.onabort = () => console.log('file reading was aborted');
        reader.onerror = () => console.log('file reading has failed');
        reader.onload = (e) => {
            setFieldValue('image_url', e.target.result);
        };
        reader.readAsDataURL(file);
    }, []);

    const cleanCategories = (values) => {
        const cleanedCategories = values.categories.filter((categoryId) => categoryId >= 0);
        return { ...values, categories: cleanedCategories };
    }

    return !loaded ? (
        <LoadingLogo styleClassName={classes.loadingSvg} />
    ) : (
        <Formik
            validator={() => ({})}
            enableReinitialize={true}
            initialValues={{ ...initValues }}
            validationSchema={Yup.object({
                display_name: Yup.string().max(150, 'Must be 150 characters or less').required('Required'),
                description: Yup.string().max(255, 'Must be 255 characters or less').required('Required'),
                disclaimer_tooltip: Yup.string().max(255, 'Must be 255 characters or less'),
                disclaimer_html: Yup.string(),
                custom_data: Yup.string(),
                config_json: Yup.array(),
                asset_link_types: Yup.array(),
                groups: Yup.array(),
                categories: Yup.array(),
                audiences: Yup.array(),
                nav_hotspot_icon_id: Yup.string(),
                item_hotspot_icon_id: Yup.string(),
                image: Yup.mixed(),
                empty_item_opacity: Yup.number().integer('Please enter a whole number for Empty Item Opacity.').min(0, 'Empty Item Opacity must be 0 or greater.').max(100, 'Empty Item Opacity must be 100 or less.'),
                hide_empty_item_panel: Yup.boolean()
            })}
            onSubmit={(values, { setSubmitting, resetForm }) => {
                handleSubmit(values, setSubmitting, resetForm);
            }}
        >
            {({ values, errors, touched, isSubmitting, setFieldValue, onBlur }) => (
                <Form className={classes.form}>
                    <div className={classes.formHeader}>Asset Details</div>
                    <FormTextField
                        label="Asset Name"
                        name="display_name"
                        placeholder="Asset Name"
                        disabled={readOnlyMode}
                    />
                    <FormTextField
                        label="Description"
                        name="description"
                        placeholder="Description"
                        disabled={readOnlyMode}
                    />
                    <FormTextField
                        label="Disclaimer Tooltip"
                        name="disclaimer_tooltip"
                        placeholder="Disclaimer Tooltip"
                        disabled={readOnlyMode}
                    />
                    <InputLabel style={{ padding: '6px' }}>Disclaimer Text</InputLabel>
                    <Field name="disclaimer_html" style={{ marginTop: '10px' }}>
                        {({ form: { setFieldValue } }) => (
                            <TinyMCEEditor
                                id="disclaimer_html"
                                tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
                                init={{
                                    ...init,
                                }}
                                textareaName="disclaimer_html"
                                onBlur={onBlur}
                                onEditorChange={(content) => {
                                    setFieldValue('disclaimer_html', content);
                                }}
                                value={values.disclaimer_html}
                            />
                        )}
                    </Field>
                    <FormTextField
                        label="Custom Data"
                        name="custom_data"
                        placeholder="Custom Data"
                        disabled={readOnlyMode}
                    />
                    <FormTextField
                        label="Player URL"
                        name="player_url"
                        placeholder="Player URL"
                        disabled={readOnlyMode || ![Role.IPSAdmin, Role.Admin].includes(activeUser?.role)}
                    />
                    <Field className={classes.input} name="categories">
                        {({ field: { value }, form: { setFieldValue } }) => (
                            <SelectionAutocomplete
                                title="Categories"
                                placeholder="None"
                                keyProperty="category_id"
                                nameProperty="display_name"
                                entityIds={value}
                                setEntityIds={setFieldValue}
                                entitySelector={getAllCategories}
                                entityStatusSelector={getCategoryStatus}
                                fetchEntityPage={fetchAllCategories}
                                formField={'categories'}
                                enableCreatable
                                creatableDialogTitle={'category'}
                                creatableDialogFieldLabel={'Category'}
                                creatableDispatch={createCategory}
                                fieldValidationLength={150}
                                fetchParams={{ page: 1, pageSize: 100 }}
                                readOnly={readOnlyMode}
                                creatableParams={{
                                    asset_type_id: AssetTypes.Product,
                                }}
                            />
                        )}
                    </Field>
                    {formAction.id === FormActions.Edit.id && (
                        <Field className={classes.input} name="companion_apps">
                            {({ field: { value }, form: { setFieldValue } }) => (
                                <SelectionAutocomplete
                                    title="Companion Apps"
                                    placeholder="Companion Apps"
                                    keyProperty="companion_app_id"
                                    nameProperty="display_name"
                                    entityIds={value}
                                    setEntityIds={setFieldValue}
                                    entitySelector={getAllCompanionApps}
                                    entityStatusSelector={getCompanionLoadingStatus}
                                    fetchEntityPage={fetchAllCompanionApps}
                                    formField={'companion_apps'}
                                    readOnly={readOnlyMode}
                                />
                            )}
                        </Field>
                    )}
                    <Field className={classes.input} name="asset_link_types">
                        {({ field: { value }, form: { setFieldValue } }) => (
                            <SelectionAutocomplete
                                title="Link Types"
                                placeholder="Add at least one link type from the check list"
                                keyProperty="link_type_id"
                                nameProperty="display_name"
                                entityIds={value}
                                setEntityIds={setFieldValue}
                                entitySelector={selectAssetLinkTypes}
                                entityStatusSelector={selectAssetLinkTypesStatus}
                                fetchEntityPage={getAllLinkTypes}
                                formField={'asset_link_types'}
                                enableCache
                                refreshCacheType="allLinkTypes"
                                refreshCacheTitle={'Refresh Link Types'}
                                readOnly={readOnlyMode}
                            />
                        )}
                    </Field>
                    <Field className={classes.input} name="groups">
                        {({ field: { value }, form: { setFieldValue } }) => (
                            <SelectionAutocomplete
                                title="Groups"
                                placeholder="None"
                                keyProperty="group_id"
                                nameProperty="display_name"
                                entityIds={value}
                                setEntityIds={setFieldValue}
                                entitySelector={selectAllGroups}
                                entityStatusSelector={getGroupStatus}
                                fetchEntityPage={fetchGroups}
                                formField={'groups'}
                                fetchParams={{ page: 1, pageSize: 100 }}
                                readOnly={readOnlyMode}
                            />
                        )}
                    </Field>
                    <Field className={classes.input} name="audiences">
                        {({ field: { value }, form: { setFieldValue } }) => (
                            <SelectionAutocomplete
                                title="Audiences"
                                placeholder="None"
                                keyProperty="audience_id"
                                nameProperty="display_name"
                                entityIds={value}
                                setEntityIds={setFieldValue}
                                entitySelector={selectAllAudiences}
                                entityStatusSelector={getAudienceStatus}
                                fetchEntityPage={fetchAudiencesAll}
                                formField={'audiences'}
                                readOnly={readOnlyMode}
                            />
                        )}
                    </Field>
                    <Field className={classes.input} name="nav_hotspot_icon_id">
                        {({ field: { value }, form: { setFieldValue } }) => (
                            <SelectionAutocomplete
                                title="Nav Hotspot"
                                placeholder="default"
                                keyProperty="hotspot_icon_id"
                                nameProperty="display_name"
                                entityIds={value}
                                setEntityIds={setFieldValue}
                                entitySelector={getNavigationHotspots}
                                entityStatusSelector={getHotspotStatus}
                                fetchEntityPage={fetchAllHotspotIcons}
                                formField={'nav_hotspot_icon_id'}
                                multiSelection={false}
                                fetchParams={{ page: 1, pageSize: 100 }}
                                errorMessage={errors['nav_hotspot_icon_id']}
                                touched={touched['nav_hotspot_icon_id']}
                                readOnly={readOnlyMode}
                            />
                        )}
                    </Field>
                    {errors['nav_hotspot_icon_id'] && (
                        <div className={classes.formErrorDiv}>
                            <ErrorMessage name="nav_hotspot_icon_id" component="div" />
                        </div>
                    )}

                    <Field className={classes.input} name="item_hotspot_icon_id">
                        {({ field: { value }, form: { setFieldValue } }) => (
                            <SelectionAutocomplete
                                title="Item Hotspot"
                                placeholder="default"
                                keyProperty="hotspot_icon_id"
                                nameProperty="display_name"
                                entityIds={value}
                                setEntityIds={setFieldValue}
                                entitySelector={getItemHotspots}
                                entityStatusSelector={getHotspotStatus}
                                fetchEntityPage={fetchAllHotspotIcons}
                                formField={'item_hotspot_icon_id'}
                                multiSelection={false}
                                fetchParams={{ page: 1, pageSize: 100 }}
                                errorMessage={errors['item_hotspot_icon_id']}
                                touched={touched['item_hotspot_icon_id']}
                                readOnly={readOnlyMode}
                            />
                        )}
                    </Field>
                    {errors['item_hotspot_icon_id'] && (
                        <div className={classes.formErrorDiv}>
                            <ErrorMessage name="item_hotspot_icon_id" component="div" />
                        </div>
                    )}
                    <FormControl fullWidth style={{ marginTop: '10px' }}>
                        <Field name="child_asset_ids">
                            {({ field: { value }, form: { setFieldValue } }) => (
                                <Autocomplete
                                    variant="outline"
                                    id="child_asset_ids"
                                    fullWidth={true}
                                    multiple
                                    limitTags={2}
                                    options={assetLists}
                                    disableCloseOnSelect
                                    getOptionLabel={(option) => option.display_name}
                                    value={assetLists.filter((asset) => value.includes(asset.asset_id))}
                                    clearIcon={<MdDelete fontSize="18" />}
                                    onChange={(_, newInputValue) => {
                                        setFieldValue(
                                            'child_asset_ids',
                                            newInputValue.map((asset) => asset.asset_id)
                                        );
                                    }}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.asset_id}>
                                            {option.display_name}
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Child Assets"
                                            placeholder="Child Assets"
                                        />
                                    )}
                                    readOnly={readOnlyMode}
                                />
                            )}
                        </Field>
                    </FormControl>
                    <FormControl fullWidth style={{ marginTop: '10px' }}>
                        <FormTextField
                            type="number"
                            label="Set empty item opacity (100% visible • 0% hidden)"
                            name="empty_item_opacity"
                            placeholder="Set empty item opacity"
                            disabled={readOnlyMode}
                        />
                    </FormControl>
                     <FormTextField
                        component={CheckboxWithLabel}
                        Label={{ label: 'Do not show the content panel for empty items' }}
                        type="checkbox"
                        checked={!!values?.hide_empty_item_panel}
                        placeholder="Do not show the content panel for empty items"
                        disabled={readOnlyMode}
                        onChange={(e) => {
                            setFieldValue('hide_empty_item_panel', e.target.checked);
                        }}
                    />
                    <FormTextField
                        component={CheckboxWithLabel}
                        Label={{ label: 'Auto view single link' }}
                        type="checkbox"
                        checked={!!values?.auto_view_single_link}
                        placeholder="Auto view single link"
                        disabled={readOnlyMode}
                        indeterminate={values?.auto_view_single_link === undefined ? true : false}
                        onChange={(e) => {
                            setFieldValue('auto_view_single_link', e.target.checked);
                        }}
                    />
                    <Accordion style={{ padding: '0px' }} className={clsx(classes.input, styles.accordion)}>
                        <AccordionSummary
                            className={styles.customAccordionSummary}
                            expandIcon={<MdExpandMore className="react-icon" />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                        >
                            <Typography className={styles.heading}>Key Value Pairs</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <ConfigValuesField values={values} fieldName="config_json" />
                        </AccordionDetails>
                    </Accordion>
                    <fieldset
                        style={{
                            border: '1px solid #bdc3c7',
                            borderRadius: '5px',
                            paddingLeft: '10px',
                            color: '#000000',
                        }}
                    >
                        <legend
                            style={{
                                marginLeft: '10px',
                                padding: '3px',
                                fontSize: '14px',
                                color: '#6d7178',
                            }}
                        >
                            Navigation Bar Options
                        </legend>
                        <FormControlLabel
                            label="Hide zones"
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={Boolean(values?.hide_nav_zones)}
                                    onChange={(e) => {
                                        setFieldValue('hide_nav_zones', e.target.checked);
                                    }}
                                    disabled={readOnlyMode}
                                />
                            }
                        />
                        <FormControlLabel
                            label="Hide search"
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={Boolean(values?.hide_nav_search)}
                                    onChange={(e) => {
                                        setFieldValue('hide_nav_search', e.target.checked);
                                    }}
                                    disabled={readOnlyMode}
                                />
                            }
                        />
                        <FormControlLabel
                            label="Hide title"
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={Boolean(values?.hide_nav_title)}
                                    onChange={(e) => {
                                        setFieldValue('hide_nav_title', e.target.checked);
                                    }}
                                    disabled={readOnlyMode}
                                />
                            }
                        />
                        <FormControlLabel
                            label="Hide controls"
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={Boolean(values?.hide_nav_controls)}
                                    onChange={(e) => {
                                        setFieldValue('hide_nav_controls', e.target.checked);
                                    }}
                                    disabled={readOnlyMode}
                                />
                            }
                        />
                        <FormControlLabel
                            label="Hide Layout Panel"
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={Boolean(values?.hide_viewer_layout_panel)}
                                    onChange={(e) => {
                                        setFieldValue('hide_viewer_layout_panel', e.target.checked);
                                    }}
                                    disabled={readOnlyMode}
                                />
                            }
                        />
                    </fieldset>
                    <Field className={classes.input} name="image" type="file">
                        {({ field: { value }, form: { setFieldValue } }) => (
                            <div className={panelClasses.dropzoneContainer}>
                                <Dropzone
                                    accept={LinkTypes.Image.contentTypes.map((at) => at.extension)}
                                    maxFiles={1}
                                    maxSize={LinkTypes.Image.fileSizeLimit}
                                    multiple={false}
                                    onDropAccepted={(files) => {
                                        if (!readOnlyMode) {
                                            onDrop({
                                                file: files[0],
                                                setFieldValue: setFieldValue,
                                            });
                                        }
                                    }}
                                    noClick={readOnlyMode}
                                >
                                    {({ getRootProps, getInputProps }) => (
                                        <div
                                            {...getRootProps()}
                                            className={panelClasses.dropzoneArea}
                                            style={{
                                                backgroundImage: `url(${values.image_url})`,
                                                backgroundRepeat: 'no-repeat',
                                                backgroundSize: 'contain',
                                                backgroundPosition: 'center center',
                                            }}
                                        >
                                            <input {...getInputProps()} />
                                            {!values.image_url && (
                                                <div className={panelClasses.dropzoneText}>
                                                    Drop Asset Preview Image Here Or Click To Browse
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Dropzone>
                                <div className={panelClasses.previewContainer}>
                                    {value && Object.keys(value).length > 0 && (
                                        <Chip
                                            className={panelClasses.previewUploadFileName}
                                            color="secondary"
                                            size="small"
                                            deleteIcon={<MdClose className="react-icon" />}
                                            onDelete={() => {
                                                if (!readOnlyMode) {
                                                    setFieldValue('image', {});
                                                    setFieldValue('image_url', '');
                                                }
                                            }}
                                            label={value?.name}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </Field>
                    {readOnlyMode !== true && (
                        <>
                            <SetDirtyForm />
                            <button className={classes.submit} type="submit" disabled={isSubmitting}>
                                {formAction.buttonLabel}
                            </button>
                        </>
                    )}
                </Form>
            )}
        </Formik>
    );
};

export { AssetForm };
